import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { CHANGE_CAUSE_KEY, CHANGE_KIND_KEY } from '../lib/labels'
import { formatMoney, isNegativeMoney } from '../lib/money'
import type { CostChange } from '../types'
import DetailRow from '../components/DetailRow'

const DASH = '—'

interface Props { change: CostChange; projectId: string; currency: string; locale: string }

const ChangeSummaryCard: React.FC<Props> = ({ change, projectId, currency, locale }) => {
  const { t } = useTranslation()
  const when = (name: string | null, at: string | null) => (at ? `${name ?? DASH} · ${new Date(at).toLocaleString(t('date_locale') || 'cs-CZ')}` : DASH)
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader><CardTitle className="text-base">{t('CostControl_change_details_title')}</CardTitle></CardHeader>
      <CardContent>
        <dl className="divide-y">
          <DetailRow label={t('CostControl_change_field_kind')}>{t(CHANGE_KIND_KEY[change.kind])}</DetailRow>
          <DetailRow label={t('CostControl_change_field_contract')}>
            {change.contractId ? (
              <Link className="text-primary hover:underline" to={`/project/${projectId}/cost-control/contracts/${change.contractId}`}>
                {change.contractNumber ? `${change.contractNumber} · ${change.contractName}` : change.contractName}
              </Link>
            ) : DASH}
          </DetailRow>
          {change.rfqId && (
            <DetailRow label={t('CostControl_rfq_source')}>
              <Link className="text-primary hover:underline" to={`/project/${projectId}/cost-control/rfqs/${change.rfqId}`}>{t('CostControl_rfq_from', { number: change.rfqNumber })}</Link>
            </DetailRow>
          )}
          {(change.cause || change.kind === 'POTENTIAL') && (
            <DetailRow label={t('CostControl_change_field_cause')}>{change.cause ? t(CHANGE_CAUSE_KEY[change.cause]) : DASH}</DetailRow>
          )}
          {change.sourcedRfqId && (
            <DetailRow label={t('CostControl_rfq_source')}>
              <Link className="text-primary hover:underline" to={`/project/${projectId}/cost-control/rfqs/${change.sourcedRfqId}`}>{t('CostControl_potential_rfq_link', { number: change.sourcedRfqNumber })}</Link>
            </DetailRow>
          )}
          {change.convertedFromPotentialAt && (
            <DetailRow label={t('CostControl_potential_origin')}>
              {new Date(change.convertedFromPotentialAt).toLocaleString(t('date_locale') || 'cs-CZ')}
              {change.status === 'DRAFT' && <p className="text-xs text-muted-foreground">{t('CostControl_potential_still_counts')}</p>}
            </DetailRow>
          )}
          <DetailRow label={t('CostControl_change_col_total')}>
            <strong className={cn('tabular-nums', isNegativeMoney(change.total) && 'text-destructive')}>{formatMoney(change.total, { locale, currency })}</strong>
          </DetailRow>
          <DetailRow label={t('CostControl_change_field_reason')}><span className="whitespace-pre-wrap">{change.reason || DASH}</span></DetailRow>
          <DetailRow label={t('CostControl_change_field_created')}>{when(change.createdByName, change.createdAt)}</DetailRow>
          <DetailRow label={t('CostControl_change_field_submitted')}>{when(change.submittedByName, change.submittedAt)}</DetailRow>
          <DetailRow label={t('CostControl_change_field_decided')}>{when(change.decidedByName, change.decidedAt)}</DetailRow>
          {change.decisionNote && (
            <DetailRow label={change.kind === 'POTENTIAL' ? t('CostControl_potential_dismiss_note') : t('CostControl_change_field_decision_note')}><span className="whitespace-pre-wrap">{change.decisionNote}</span></DetailRow>
          )}
        </dl>
      </CardContent>
    </Card>
  )
}
export default ChangeSummaryCard
